import { Fragment, useRef, useState } from 'react';
import type { FC, ReactNode, CSSProperties, PropsWithChildren } from 'react';
import classnames from 'classnames';
import {
  useFloating,
  arrow,
  autoUpdate,
  useHover,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
  flip,
  shift,
  offset,
  safePolygon,
  useTransitionStatus,
  FloatingPortal
} from '@floating-ui/react';

import { getOffset, getPlacement } from 'app/utils/floating-ui';

import style from './style.scss';

interface Props extends PropsWithChildren {
  position?: Position;
  alignment?: Alignment;
  content: ReactNode;
  offset?: { x: number; y: number };
  withoutArrow?: boolean;
  className?: string;
  style?: CSSProperties;
  instantExit?: boolean;
  withPortal?: boolean;
  active?: boolean;
  contentClassName?: string;
  withContainer?: boolean;
  keepReferenceWhenInactive?: boolean;
}

const Tooltip: FC<Props> = ({
  children,
  content,
  position = 'top',
  alignment = 'middle',
  offset: definedOffset,
  withoutArrow = false,
  className,
  style: css,
  instantExit = false,
  withPortal = false,
  active = true,
  contentClassName,
  withContainer = false,
  keepReferenceWhenInactive = false
}) => {
  const arrowRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);

  const { x, y, reference, floating, strategy, context, placement, middlewareData } = useFloating({
    middleware: [
      offset(getOffset(definedOffset, position)),
      flip(),
      shift(),
      arrow({ element: arrowRef })
    ],
    open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    placement: getPlacement(position, alignment)
  });

  const hover = useHover(context, {
    move: false,
    delay: instantExit ? 0 : 200,
    handleClose: safePolygon({ buffer: instantExit ? 0 : 0.5 })
  });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'tooltip' });

  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role]);

  const { isMounted, status } = useTransitionStatus(context, { duration: 300 });

  const { x: arrowX, y: arrowY } = middlewareData?.arrow || { x: undefined, y: undefined };

  const tooltipClassName = classnames(style.tooltip, {
    [style.top]: placement.includes('top'),
    [style.left]: placement.includes('left'),
    [style.right]: placement.includes('right'),
    [style.bottom]: placement.includes('bottom')
  });

  const portalClassName = classnames(style.portal, {
    [style.top]: placement.includes('top'),
    [style.left]: placement.includes('left'),
    [style.right]: placement.includes('right'),
    [style.bottom]: placement.includes('bottom')
  });

  const Wrapper = withPortal ? FloatingPortal : Fragment;

  if (!active && !keepReferenceWhenInactive) {
    if (withContainer) {
      return (
        <div className={classnames(style.container, className)} style={css}>
          {children}
        </div>
      );
    }

    return <>{children}</>;
  }

  return (
    <>
      <div
        ref={reference}
        className={classnames(style.container, className)}
        style={css}
        {...getReferenceProps()}
      >
        {children}
      </div>
      {isMounted && (!keepReferenceWhenInactive || active) && (
        <Wrapper>
          <div
            ref={floating}
            style={{
              position: strategy,
              top: y ?? 0,
              left: x ?? 0,
              width: 'max-content'
            }}
            {...getFloatingProps()}
            className={portalClassName}
            data-status={status}
          >
            <div className={tooltipClassName}>
              {!withoutArrow && (
                <div
                  style={{
                    left: arrowX !== undefined ? `${arrowX}px` : '',
                    top: arrowY !== undefined ? `${arrowY}px` : ''
                  }}
                  ref={arrowRef}
                  className={style.triangle}
                />
              )}
              <div className={classnames(style.content, contentClassName)}>{content}</div>
            </div>
          </div>
        </Wrapper>
      )}
    </>
  );
};

Tooltip.displayName = 'Tooltip';

export default Tooltip;
